import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import {
  DeleteIndicatorMeasurementRequestParamSchema,
  IndicatorMeasurementFormSchema,
  MeasurementsModel,
  UpdateIndicatorMeasurementRequestParamSchema,
} from '../models';

export const useIndicatorMeasurements = <T = z.infer<typeof MeasurementsModel>>(
  select?: (data: z.infer<typeof MeasurementsModel>) => T,
) => {
  return useQuery({
    queryKey: ['indicator-measurements'],
    queryFn: async () => {
      const res = await axiosGet('/api/measurements/');
      return MeasurementsModel.parse(res);
    },
    select,
  });
};

export const useCreateIndicatorMeasurement = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      data: z.infer<typeof IndicatorMeasurementFormSchema>,
    ) => {
      const parsed = IndicatorMeasurementFormSchema.parse(data);
      const res = await axiosPost('/api/measurements/', parsed);
      await invalidate(['indicator-measurements']);
      return res;
    },
  });
};

export const useUpdateIndicatorMeasurement = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      data: z.infer<typeof UpdateIndicatorMeasurementRequestParamSchema>,
    ) => {
      const { id, ...parsed } =
        UpdateIndicatorMeasurementRequestParamSchema.parse(data);
      const res = await axiosPatch(`/api/measurements/${id}/`, parsed);
      await invalidate(['indicator-measurements']);
      return res;
    },
  });
};

export const useDeleteIndicatorMeasurement = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      data: z.infer<typeof DeleteIndicatorMeasurementRequestParamSchema>,
    ) => {
      const { id } = DeleteIndicatorMeasurementRequestParamSchema.parse(data);
      const res = await axiosDelete(`/api/measurements/${id}/`);
      await invalidate(['indicator-measurements']);
      return res;
    },
  });
};
